import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  FaChevronDown,
  FaCheck,
  FaExclamationCircle,
  FaInfoCircle,
  FaUsers,
  FaBuilding,
  FaCoins,
  FaCalculator,
  FaMagic,
  FaSearch,
  FaUndo
} from "react-icons/fa";
import { apiService } from "../../../components/Config/api.service";
import AccountSelectField from "./Account/AccountSelectField";
import CreateAccountModal from "./Account/CreateAccountModal";


const Accounting = ({
  categorizedRules,
  setCategorizedRules,
  categoriesMeta,
  setCategoriesMeta,
  defaultAccounts,
  setDefaultAccounts,
  onSaved,
  initialSearch = "",
}) => {
  const { t } = useTranslation();
  // Recherche et filtre : n'agissent que sur l'affichage, jamais sur ce qui est enregistré.
  const [search, setSearch] = useState(initialSearch);
  const [filter, setFilter] = useState("all"); // 'all' | 'todo' | 'own' | 'inherited'
  const [dirty, setDirty] = useState(false);
  const [expanded, setExpanded] = useState({
    chs: true,
    chp: true,
    brut: true,
    ded: true,
    noim: true,
    totaux: true,
    other: true,
    default: true,
    showExceptions: false
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [accountTypes, setAccountTypes] = useState([]);

  // États pour la modal de création de compte
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newAccountData, setNewAccountData] = useState({
    code: "",
    name: "",
    account_type: "",
    reconcile: false,
  });
  const [pendingSelection, setPendingSelection] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);

  // Charge les types de comptes une seule fois
  React.useEffect(() => {
    apiService.getElement("/accounting/account_types").then(res => {
      if (res.data) setAccountTypes(res.data);
    });
  }, []);

  // Configuration des catégories
  const categoryConfig = {
    chs: {
      title: t('accountingSettings.categoryEmployeeCharges'),
      icon: <FaUsers className="text-red-600" />,
      color: "red",
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
      textColor: "text-red-900",
      description: t('accountingSettings.categoryEmployeeChargesDesc')
    },
    chp: {
      title: t('accountingSettings.categoryEmployerCharges'),
      icon: <FaBuilding className="text-orange-600" />,
      color: "orange",
      bgColor: "bg-orange-50",
      borderColor: "border-orange-200",
      textColor: "text-orange-900",
      description: t('accountingSettings.categoryEmployerChargesDesc')
    },
    brut: {
      title: t('accountingSettings.categoryGross'),
      icon: <FaCoins className="text-blue-600" />,
      color: "blue",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      textColor: "text-blue-900",
      description: t('accountingSettings.categoryGrossDesc')
    },
    ded: {
      title: t('accountingSettings.categoryDeductions'),
      icon: <FaCalculator className="text-teal-600" />,
      color: "teal",
      bgColor: "bg-teal-50",
      borderColor: "border-teal-200",
      textColor: "text-teal-900",
      description: t('accountingSettings.categoryDeductionsDesc')
    },
    noim: {
      title: t('accountingSettings.categoryNonTaxable'),
      icon: <FaCalculator className="text-yellow-600" />,
      color: "yellow",
      bgColor: "bg-yellow-50",
      borderColor: "border-yellow-200",
      textColor: "text-yellow-900",
      description: t('accountingSettings.categoryNonTaxableDesc')
    },
    totaux: {
      title: t('accountingSettings.categoryNet'),
      icon: <FaCalculator className="text-green-600" />,
      color: "green",
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
      textColor: "text-green-900",
      description: t('accountingSettings.categoryNetDesc')
    },
    default: {
      title: t('accountingSettings.categoryDefaultCodes'),
      icon: <FaMagic className="text-purple-600" />,
      color: "purple",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200",
      textColor: "text-purple-900",
      description: t('accountingSettings.categoryDefaultCodesDesc')
    },
  };

  // Fonction pour rechercher les comptes comptables
  const loadAccountOptions = async (inputValue) => {
    if (inputValue.length < 2) return [];

    try {
      const response = await apiService.getElement(`/accounting/accounts/search?q=${inputValue}`);
      const options = response.data.map((account) => ({
        value: account.code,
        label: `${account.code} - ${account.name}`,
        account: account,
        company_id: account.company_id,
        company_name: account.company_name,
        isParentAccount: account.is_parent_account,
      }));

      if (options.length === 0 && /^[0-9]+$/.test(inputValue)) {
        options.push({
          value: inputValue,
          label: `${inputValue} - ${t('accountingSettings.createThisAccount')}`,
          isCreateOption: true,
        });
      }

      return options;
    } catch (error) {
      console.error("Erreur lors de la recherche des comptes:", error);
      return [];
    }
  };

  const handleSelectChange = (ruleId, field, selectedOption, category) => {
    if (selectedOption && selectedOption.isCreateOption) {
      setNewAccountData({
        code: selectedOption.value,
        name: "",
        account_type: "",
        reconcile: false,
      });
      setPendingSelection({ ruleId, field, code: selectedOption.value, category });
      setShowCreateModal(true);
    } else {
      const value = selectedOption ? selectedOption.value : "";
      const sourceField = `${field}_source`; // account_debit → account_debit_source
      const originField = `${field}_origin`;
      setDirty(true);
      
      setCategorizedRules(prev => ({
        ...prev,
        [category]: prev[category].map(rule =>
          rule.id === ruleId 
            ? { ...rule, [field]: value, [sourceField]: '', [originField]: value ? 'edited' : '' } // Réinitialiser la source quand on change
            : rule
        )
      }));
      
      // Si on modifie un code par défaut, on met aussi à jour defaultAccounts pour l'envoi
      if (category === "default") {
        const rule = categorizedRules.default.find(r => r.id === ruleId);
        if (rule) {
          // Mapping plus robuste: utiliser l'ID de la règle (contient la clé)
          // Format du rule.id: 'default_prime et avantage', 'default_deduction', etc.
          const ruleIdStr = String(rule.id);
          let apiKey = null;
          
          // Extraire la clé API depuis l'ID
          if (ruleIdStr.startsWith('default_')) {
            const displayKey = ruleIdStr.replace('default_', '');
            const displayKeyToApiKey = {
              "prime imposable": "prime_imposable",
              "prime non imposable": "prime_non_imposable",
              "avantage": "avantage",
              "deduction": "deduction",
              "note de frais": "note",
              "salaire stagiaire/prestataire": "external",
              "taxe Stagiaire Prestataire": "external_tax",
            };
            apiKey = displayKeyToApiKey[displayKey];
          }
          
          if (apiKey) {
            setDefaultAccounts(prev => {
              const prevObj = prev[apiKey] || {};
              return {
                ...prev,
                [apiKey]: {
                  ...prevObj,
                  [field === "account_debit" ? "debit" : "credit"]: value,
                  // On garde l'autre champ si déjà présent
                }
              };
            });
          }
        }
      }
    }
  };

  const allNonDefaultRules = Object.entries(categorizedRules)
    .filter(([cat]) => cat !== "default")
    .flatMap(([, rules]) => rules);

  const mandatoryRules = allNonDefaultRules.filter(r => !r.is_handled_by_default);
  const handledByDefaultRules = allNonDefaultRules.filter(r => r.is_handled_by_default);

  const groupRulesByCategory = (rulesList) => {
    return rulesList.reduce((acc, rule) => {
      if (!acc[rule.category]) acc[rule.category] = [];
      acc[rule.category].push(rule);
      return acc;
    }, {});
  };

  const mandatoryGroups = groupRulesByCategory(mandatoryRules);
  const defaultHandledGroups = groupRulesByCategory(handledByDefaultRules);

  const normalize = (value) => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const sides = (rule) => (rule.has_debit === false ? ["account_credit"] : ["account_debit", "account_credit"]);
  const isIncomplete = (rule) => sides(rule).every((field) => !rule[field]);
  const hasOrigin = (rule, origins) => sides(rule).some((field) => origins.includes(rule[`${field}_origin`]));
  const searchTerm = normalize(search.trim());
  const isFiltering = Boolean(searchTerm) || filter !== "all";
  const matchesFilters = (rule) => {
    if (searchTerm && !normalize(`${rule.name} ${rule.code}`).includes(searchTerm)) return false;
    if (filter === "todo") return isIncomplete(rule);
    if (filter === "own") return hasOrigin(rule, ["company", "edited"]);
    if (filter === "inherited") return hasOrigin(rule, ["parent", "rule", "default"]);
    return true;
  };
  const completedCount = mandatoryRules.filter((rule) => !isIncomplete(rule)).length;
  const visibleCount = mandatoryRules.filter(matchesFilters).length;
  const visibleDefaults = (categorizedRules.default || []).filter(matchesFilters);

  // « Rétablir l'héritage » : le champ reprend la valeur héritée ; à l'enregistrement,
  // le serveur retire alors le compte propre à la société.
  const restoreInheritance = (rule, field, category) => {
    handleSelectChange(rule.id, field, { value: rule[`${field}_inherited`] || "" }, category);
    setCategorizedRules(prev => ({
      ...prev,
      [category]: prev[category].map(r => (r.id === rule.id ? { ...r, [`${field}_origin`]: 'restored' } : r)),
    }));
  };

  const OriginBadge = ({ rule, field, category }) => {
    const origin = rule[`${field}_origin`];
    if (!origin) return null;
    const labels = {
      company: t('accountingSettings.originCompany'),
      parent: t('accountingSettings.originParent', { source: rule[`${field}_source`] || '' }),
      rule: t('accountingSettings.originRule'),
      default: t('accountingSettings.originDefault'),
      edited: t('accountingSettings.originEdited'),
      restored: t('accountingSettings.originRestored'),
    };
    const tone = origin === 'company' ? 'bg-teal-50 text-teal-800 border-teal-200'
      : (origin === 'edited' || origin === 'restored') ? 'bg-amber-50 text-amber-800 border-amber-200'
      : 'bg-gray-50 text-gray-600 border-gray-200';
    return (
      <div className="mt-1 flex items-center gap-2 text-[11px]" data-cy={`origin-${field}`}>
        <span className={`px-2 py-0.5 rounded-full border ${tone}`}>{labels[origin]}</span>
        {origin === 'company' && (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-[#0C5B6D] hover:underline"
            onClick={() => restoreInheritance(rule, field, category)}
            title={rule[`${field}_inherited`] ? t('accountingSettings.restoreInheritanceTo', { value: rule[`${field}_inherited`] }) : t('accountingSettings.restoreInheritanceEmpty')}
          >
            <FaUndo size={9} />
            {t('accountingSettings.restoreInheritance')}
          </button>
        )}
      </div>
    );
  };

  const CategorySection = ({ category, rules: allCategoryRules, isException = false }) => {
    const config = categoryConfig[category];
    const categoryRules = (allCategoryRules || []).filter(matchesFilters);
    if (!config || categoryRules.length === 0) return null;
    // Une recherche ou un filtre déplie les sections qui ont un résultat.
    const isExpanded = isFiltering || expanded[category];

    return (
      <div className="space-y-4">
        <div
          className={`flex items-center justify-between p-4 ${config.bgColor} ${config.borderColor} border rounded-lg cursor-pointer hover:shadow-md transition-all duration-200`}
          onClick={() =>
            setExpanded((prev) => ({ ...prev, [category]: !prev[category] }))
          }
        >
          <div className="flex items-center gap-3">
            <div>
              <h5 className={`font-semibold ${config.textColor}`}>
                {config.title} ({categoryRules.length})
              </h5>
              <p className={`text-sm ${config.textColor} opacity-75`}>
                {config.description}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className={`${config.textColor} opacity-60 transition-transform duration-200`} 
                 style={{ transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}>
              <FaChevronDown />
            </div>
          </div>
        </div>
        {isExpanded && (
          <div className="pl-4 space-y-2">
            <div className="space-y-2">
              {categoryRules.map((rule) => {
                const isFieldEmpty = isIncomplete(rule);
                const containerClass = isException && isFieldEmpty
                  ? "border border-gray-200 rounded-lg px-4 py-3 transition-all duration-200 bg-gray-50 opacity-80"
                  : "border border-gray-200 rounded-lg px-4 py-3 hover:shadow-sm transition-all duration-200 bg-white";
                return (
                  <div key={rule.id} className={containerClass} data-cy="accounting-rule-row">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
                      <div className="lg:col-span-4">
                        <h6 className="font-semibold text-gray-900">{rule.name}</h6>
                        {rule.code && <p className="text-xs text-gray-500">{rule.code}</p>}
                        {isFieldEmpty && !isException && (
                          <span className="mt-1 inline-block text-[11px] bg-orange-50 text-orange-800 border border-orange-200 px-2 py-0.5 rounded-full">
                            {t('accountingSettings.toComplete')}
                          </span>
                        )}
                        {isException && isFieldEmpty && (
                          <span className="mt-1 inline-block text-xs bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-medium shadow-sm">
                            {t('accountingSettings.handledByDefault')}
                          </span>
                        )}
                      </div>
                      {/* Pas de compte de débit pour la taxe stagiaire/prestataire : le serveur l'ignore. */}
                      <div className="lg:col-span-4">
                        {rule.has_debit !== false && (
                          <>
                            <AccountSelectField
                              label={t('accountingSettings.debitAccount')}
                              value={rule.account_debit}
                              onChange={(selectedOption) =>
                                handleSelectChange(rule.id, "account_debit", selectedOption, category)
                              }
                              placeholder={isException && rule.default_debit ? t('accountingSettings.defaultPlaceholder', { value: rule.default_debit }) : t('accountingSettings.search')}
                              loadAccountOptions={loadAccountOptions}
                            />
                            <OriginBadge rule={rule} field="account_debit" category={category} />
                          </>
                        )}
                      </div>
                      <div className="lg:col-span-4">
                        <AccountSelectField
                          label={t('accountingSettings.creditAccount')}
                          value={rule.account_credit}
                          onChange={(selectedOption) =>
                            handleSelectChange(rule.id, "account_credit", selectedOption, category)
                          }
                          placeholder={isException && rule.default_credit ? t('accountingSettings.defaultPlaceholder', { value: rule.default_credit }) : t('accountingSettings.search')}
                          loadAccountOptions={loadAccountOptions}
                        />
                        <OriginBadge rule={rule} field="account_credit" category={category} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const allRules = Object.entries(categorizedRules)
        .filter(([cat]) => cat !== "default")
        .map(([, rules]) => rules)
        .flat();

      const rulesPayload = allRules.map(rule => ({
        id: rule.id,
        account_debit: rule.account_debit || "",
        account_credit: rule.account_credit || "",
      }));

      await apiService.saveElement("/accounting/codes", {
        rules: rulesPayload,
        default_accounts: defaultAccounts,
      });

      setMessage({ type: "success", text: t('accountingSettings.codesSavedSuccess') });
      setDirty(false);
      if (onSaved) await onSaved();
    } catch (err) {
      // Code de compte inconnu : le serveur refuse tout l'enregistrement et nomme les codes.
      const unknownCodes = err?.response?.data?.unknown_codes;
      setMessage({
        type: "error",
        text: unknownCodes?.length
          ? t('accountingSettings.unknownCodes', { codes: unknownCodes.join(', ') })
          : t('accountingSettings.saveError'),
      });
    } finally {
      setLoading(false);
    }
  };

  // Recharger ou fermer l'onglet du navigateur demande confirmation. La navigation
  // interne (autre onglet des paramètres, autre page) n'est pas bloquée.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const filterOptions = useMemo(() => ([
    { id: 'all', label: t('accountingSettings.filterAll') },
    { id: 'todo', label: t('accountingSettings.filterTodo') },
    { id: 'own', label: t('accountingSettings.filterOwn') },
    { id: 'inherited', label: t('accountingSettings.filterInherited') },
  ]), [t]);

  // Fonctions de gestion des inputs avec useCallback pour éviter les re-renders
  const handleCodeChange = useCallback((e) => {
    setNewAccountData(prev => ({ ...prev, code: e.target.value }));
  }, []);

  const handleNameChange = useCallback((e) => {
    setNewAccountData(prev => ({ ...prev, name: e.target.value }));
  }, []);

  const handleTypeChange = useCallback((selectedOption) => {
    setNewAccountData(prev => ({
      ...prev,
      account_type: selectedOption?.value || "",
    }));
  }, []);

  const handleReconcileChange = useCallback((e) => {
    setNewAccountData(prev => ({ ...prev, reconcile: e.target.checked }));
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowCreateModal(false);
    setNewAccountData({
      code: "",
      name: "",
      account_type: "",
      reconcile: false,
    });
    setPendingSelection(null);
  }, []);

  const handleCreateAccount = async () => {
    if (
      !newAccountData.code ||
      !newAccountData.name 
    ) {
      setMessage({ type: "error", text: t('accountingSettings.allFieldsRequired') });
      return;
    }

    setCreateLoading(true);
    try {
      const response = await apiService.saveElement(
        "/accounting/accounts/create",
        newAccountData
      );

      if (response.data.success) {
        if (pendingSelection) {
          // Même chemin qu'une sélection : indicateur, origine et comptes par défaut à jour.
          handleSelectChange(
            pendingSelection.ruleId,
            pendingSelection.field,
            { value: newAccountData.code },
            pendingSelection.category
          );
        }

        setMessage({ type: "success", text: t('accountingSettings.accountCreatedSuccess') });
        handleCloseModal();
      }
    } catch (error) {
      setMessage({ type: "error", text: t('accountingSettings.accountCreateError') });
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="border border-[#0C5B6D]/20 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-[#0C5B6D] rounded-full">
              <FaInfoCircle className="text-white" size={20} />
            </div>
            <div>
              <h6 className="font-semibold text-[#0C5B6D] mb-2 text-lg">
                {t('accountingSettings.introText')}
              </h6>
            </div>
          </div>
        </div>

        <div className="sticky top-0 z-20 bg-white border border-gray-200 rounded-lg p-3 shadow-sm space-y-2" data-cy="accounting-toolbar">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="relative flex-1">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('accountingSettings.searchRulePlaceholder')}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0C5B6D]/30"
                data-cy="accounting-search"
              />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {filterOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setFilter(option.id)}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${filter === option.id ? 'bg-[#0C5B6D] text-white border-[#0C5B6D]' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                  data-cy={`accounting-filter-${option.id}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-600">
            <span data-cy="accounting-completeness">
              {t('accountingSettings.completeness', { done: completedCount, total: mandatoryRules.length })}
            </span>
            {isFiltering && (
              <span>
                {t('accountingSettings.visibleRules', { count: visibleCount })}
                {' · '}
                <button type="button" className="text-[#0C5B6D] hover:underline" onClick={() => { setSearch(''); setFilter('all'); }}>
                  {t('accountingSettings.clearFilters')}
                </button>
              </span>
            )}
          </div>
        </div>

        <div className="space-y-8">
          {isFiltering && visibleCount === 0 && visibleDefaults.length === 0 && (
            <div className="text-center py-10 text-gray-500 border border-dashed rounded-lg" data-cy="accounting-no-result">
              {t('accountingSettings.noRuleMatches')}
            </div>
          )}
          {categorizedRules.default && visibleDefaults.length > 0 && (
            <div className="mb-8">
               <h3 className="text-xl font-bold text-gray-800 mb-4 border-b pb-2">{t('accountingSettings.section1Title')}</h3>
               <p className="text-gray-600 mb-4">{t('accountingSettings.section1Description')}</p>
               <CategorySection
                 key="default"
                 category="default"
                 rules={categorizedRules.default}
               />
            </div>
          )}

          {visibleCount > 0 && (
             <div className="mb-8">
               <h3 className="text-xl font-bold text-gray-800 mb-4 border-b pb-2">{t('accountingSettings.section2Title')}</h3>
               <p className="text-gray-600 mb-4 text-orange-700 bg-orange-50 p-3 rounded border border-orange-200">{t('accountingSettings.section2Description')}</p>
               <div className="space-y-6">
                 {Object.entries(mandatoryGroups).map(([cat, rules]) => (
                   <CategorySection key={cat} category={cat} rules={rules} />
                 ))}
               </div>
             </div>
          )}

          {/* 
            MASQUÉ SUR DEMANDE : Désactivation des exceptions globales pour forcer la configuration au niveau de la Prime.
            Object.keys(defaultHandledGroups).length > 0 && (
             <div className="mb-8">
               <div 
                 className="flex items-center justify-between p-4 bg-gray-100 hover:bg-gray-200 cursor-pointer rounded-lg border border-gray-300 transition-colors"
                 onClick={() => setExpanded(prev => ({...prev, showExceptions: !prev.showExceptions}))}
               >
                 <div>
                   <h3 className="text-xl font-bold text-gray-700">3. Exceptions sur les règles standards (Avancé)</h3>
                   <p className="text-sm text-gray-500 mt-1">Gérer des comptes très spécifiques pour écraser le comportement par défaut</p>
                 </div>
                 <FaChevronDown className={`text-gray-500 transition-transform duration-200 ${expanded.showExceptions ? 'rotate-180' : ''}`} size={20} />
               </div>
               
               {expanded.showExceptions && (
                 <div className="space-y-6 mt-6 animate-fadeIn">
                   <div className="bg-blue-50 border border-blue-200 p-4 rounded text-sm text-blue-800 mb-4 flex gap-3">
                     <FaInfoCircle className="shrink-0 mt-0.5 text-blue-500" size={16} />
                     <p>Les règles ci-dessous n'ont <strong>pas besoin d'être remplies</strong>. Elles sont automatiquement prises en charge par les <em>Codes par défaut</em> configurés dans l'étape 1. Ne modifiez ces champs que si vous souhaitez créer une exception pour une règle en particulier.</p>
                   </div>
                   {Object.entries(defaultHandledGroups).map(([cat, rules]) => (
                     <CategorySection key={cat} category={cat} rules={rules} isException={true} />
                   ))}
                 </div>
               )}
             </div>
           ) 
           */}
           
          {Object.keys(categorizedRules).length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <FaExclamationCircle className="mx-auto mb-3" size={24} />
              <p className="text-lg font-medium mb-1">
                {t('accountingSettings.noRuleFound')}
              </p>
            </div>
          )}
        </div>

        {/* Message */}
        {message.text && (
          <div
            className={`flex items-center gap-3 p-4 rounded-lg border-l-4 ${
              message.type === "success"
                ? "bg-green-50 border-green-500 text-green-800"
                : "bg-red-50 border-red-500 text-red-800"
            }`}
          >
            {message.type === "success" ? (
              <FaCheck className="text-green-600" size={16} />
            ) : (
              <FaExclamationCircle className="text-red-600" size={16} />
            )}
            <span className="font-medium">{message.text}</span>
          </div>
        )}

        {/* Bouton d'enregistrement */}
        <div className="sticky bottom-0 z-20 flex items-center justify-end gap-4 py-3 px-4 -mx-4 bg-white/95 backdrop-blur border-t border-gray-200" data-cy="accounting-save-bar">
          {dirty && (
            <span className="text-sm text-amber-700 flex items-center gap-2" data-cy="accounting-unsaved">
              <FaExclamationCircle size={14} />
              {t('accountingSettings.unsavedChanges')}
            </span>
          )}
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-8 py-3 bg-[#0C5B6D] text-white rounded-lg hover:bg-[#083f4c] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium shadow-lg hover:shadow-xl"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                {t('accountingSettings.savingInProgress')}
              </>
            ) : (
              <>
                <FaCheck size={16} />
                {t('recapitulatif.saveChanges')}
              </>
            )}
          </button>
        </div>
      </form>

      {/* Modal de création de compte */}
      <CreateAccountModal
        isOpen={showCreateModal}
        onClose={handleCloseModal}
        newAccountData={newAccountData}
        onCodeChange={handleCodeChange}
        onNameChange={handleNameChange}
        onTypeChange={handleTypeChange}
        onReconcileChange={handleReconcileChange}
        onCreateAccount={handleCreateAccount}
        accountTypes={accountTypes}
        createLoading={createLoading}
      />
    </>
  );
};

export default Accounting;
